import { useEffect, useState } from 'react';
import { pendingCount, drainOutbox } from '../lib/outbox';

// PWA service worker registration is handled automatically by vite-plugin-pwa
// when registerType: 'autoUpdate' is set in vite.config.ts. We DON'T import
// 'virtual:pwa-register' here — that virtual module's plugin hook trips up
// Rollup's resolver on Netlify, breaking the build. The auto-update strategy
// is fine for our use case: the new SW activates on next reload, no toast
// needed. Trade-off: users won't see an "Update ready" prompt, but they get
// the new version the moment they refresh.

export function OfflineProvider() {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [pending, setPending] = useState<number>(0);

  // Track online/offline transitions. The banner only shows when offline AND
  // after the first transition — we don't want a "you're offline" flash on
  // page load if you genuinely just landed without signal.
  const [hasGoneOffline, setHasGoneOffline] = useState(false);
  useEffect(() => {
    function on()  { setOnline(true);  }
    function off() { setOnline(false); setHasGoneOffline(true); }
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Poll the outbox count — every 5s and on focus / connectivity change.
  // Cheap (IDB read against a single index) and keeps the badge fresh.
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        const n = await pendingCount();
        if (!cancelled) setPending(n);
      } catch { /* noop */ }
    }
    void refresh();
    const t = window.setInterval(refresh, 5000);
    window.addEventListener('focus',  refresh);
    window.addEventListener('online', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(t);
      window.removeEventListener('focus',  refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);

  return (
    <>
      {/* Offline banner — only after a real online→offline transition */}
      {!online && hasGoneOffline && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 'max(env(safe-area-inset-top), 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#BF8E3D',
            color: '#FFFFFF',
            padding: '8px 14px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10000,
            pointerEvents: 'none',
          }}
        >
          Offline · your changes will sync when you're back
          {pending > 0 && <span> · {pending} pending</span>}
        </div>
      )}

      {/* Pending-sync pill — shown when online but there are queued/erroring
          entries. Tap to manually trigger a drain. Different colour from the
          offline banner so the user knows they're online but their data
          hasn't quite gone up yet. */}
      {online && pending > 0 && (
        <button
          type="button"
          onClick={() => { void drainOutbox(); }}
          aria-label={`${pending} ${pending === 1 ? 'record' : 'records'} pending sync — tap to retry`}
          style={{
            position: 'fixed',
            top: 'max(env(safe-area-inset-top), 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1E40AF',
            color: '#FFFFFF',
            padding: '8px 14px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10000,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Syncing {pending} {pending === 1 ? 'record' : 'records'}…
        </button>
      )}
    </>
  );
}
